import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SuperAdminSidebar } from '../../components/Sidebar';
import api from '../../utils/api';

const COUNTRIES = ['CH', 'DE', 'AT'];
const COUNTRY_NAMES = { CH: 'Schweiz', DE: 'Deutschland', AT: 'Österreich' };
const ROLES = [
  { value: 'imam',          label: 'Imam' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'coordinator',   label: 'Koordinator' },
  { value: 'other',         label: 'Sonstiges' },
];
const PLANS = [
  { value: 'free',     label: 'Kostenlos (Free)' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium',  label: 'Premium' },
];

export default function SuperAdminMosqueCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    mosqueName: '', countryCode: 'CH', street: '', houseNumber: '', plz: '', city: '', website: '',
    contactName: '', contactRole: 'administrator', contactEmail: '', contactPhone: '',
    loginEmail: '', initialPassword: '',
    plan: 'free', isTrial: false, months: 1,
    directApprove: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(p => ({ ...p, [e.target.name]: val }));
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setForm(p => ({ ...p, initialPassword: pw }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await api.post('/superadmin/mosques', {
        mosqueName: form.mosqueName,
        countryCode: form.countryCode,
        city: form.city,
        plz: form.plz || undefined,
        address: `${form.street} ${form.houseNumber}`.trim() || undefined,
        website: form.website || undefined,
        contactName: form.contactName,
        contactRole: form.contactRole,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone || undefined,
        loginEmail: form.loginEmail,
        initialPassword: form.initialPassword,
        plan: form.plan,
        isTrial: form.isTrial,
        months: parseInt(form.months),
        directApprove: form.directApprove,
      });
      navigate(`/superadmin/mosques/${r.data.mosque.id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <SuperAdminSidebar />
      <main className="main-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <Link to="/superadmin/mosques" className="btn btn-ghost btn-sm">← Zurück</Link>
          <div className="page-header" style={{ margin: 0 }}>
            <h1>Moschee manuell hinzufügen</h1>
            <p>Neue Moschee ohne Selbstregistrierung anlegen</p>
          </div>
        </div>

        {error && <div className="alert alert-error" style={{ maxWidth: 720, marginBottom: 20 }}>{error}</div>}

        <form onSubmit={submit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>

            {/* Moschee Info */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
                Moschee-Informationen
              </h3>

              <div className="form-group">
                <label className="form-label">Name der Moschee *</label>
                <input className="form-input" name="mosqueName" value={form.mosqueName} onChange={set} required placeholder="z.B. Islamisches Zentrum Zürich" />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Land *</label>
                <select className="form-select" name="countryCode" value={form.countryCode} onChange={set}>
                  {COUNTRIES.map(c => <option key={c} value={c}>{COUNTRY_NAMES[c]}</option>)}
                </select>
              </div>

              <div className="grid-2" style={{ gap: 16, marginTop: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Strasse *</label>
                  <input className="form-input" name="street" value={form.street} onChange={set} required placeholder="Musterstrasse" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Hausnummer *</label>
                  <input className="form-input" name="houseNumber" value={form.houseNumber} onChange={set} required placeholder="12" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">PLZ *</label>
                  <input className="form-input" name="plz" value={form.plz} onChange={set} required placeholder="8001" maxLength={10} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Stadt *</label>
                  <input className="form-input" name="city" value={form.city} onChange={set} required placeholder="Zürich" />
                </div>
                <div className="form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                  <label className="form-label">Website</label>
                  <input className="form-input" name="website" value={form.website} onChange={set} placeholder="https://..." />
                </div>
              </div>
            </div>

            {/* Kontakt */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
                Kontaktperson
              </h3>
              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Vollständiger Name *</label>
                  <input className="form-input" name="contactName" value={form.contactName} onChange={set} required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Funktion *</label>
                  <select className="form-select" name="contactRole" value={form.contactRole} onChange={set}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">E-Mail *</label>
                  <input className="form-input" type="email" name="contactEmail" value={form.contactEmail} onChange={set} required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Telefon</label>
                  <input className="form-input" name="contactPhone" value={form.contactPhone} onChange={set} placeholder="+41 ..." />
                </div>
              </div>
            </div>

            {/* Zugangsdaten */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
                Zugangsdaten
              </h3>
              <div className="form-group">
                <label className="form-label">Login-E-Mail *</label>
                <input className="form-input" type="email" name="loginEmail" value={form.loginEmail} onChange={set} required placeholder="admin@moschee.ch" />
              </div>
              <div className="form-group">
                <label className="form-label">Temporäres Passwort *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="form-input" style={{ fontFamily: 'monospace', flex: 1 }}
                    name="initialPassword" value={form.initialPassword} onChange={set}
                    required minLength={8}
                    placeholder="Mindestens 8 Zeichen"
                  />
                  <button type="button" className="btn btn-secondary" onClick={generatePassword}>Generieren</button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                  Moschee muss das Passwort beim ersten Login ändern.
                </div>
              </div>
            </div>

            {/* Plan & Status */}
            <div className="card">
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
                Plan & Status
              </h3>
              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Plan</label>
                  <select className="form-select" name="plan" value={form.plan} onChange={set}>
                    {PLANS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Laufzeit (Monate)</label>
                  <input className="form-input" type="number" name="months" value={form.months} onChange={set} min={1} max={24} />
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" name="isTrial" checked={form.isTrial} onChange={set} />
                  Als Testphase markieren
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" name="directApprove" checked={form.directApprove} onChange={set} />
                  Sofort freischalten (Status: Aktiv)
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                {loading ? 'Erstellen...' : 'Moschee erstellen'}
              </button>
              <Link to="/superadmin/mosques" className="btn btn-ghost btn-lg">Abbrechen</Link>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
